import Banner from '@/components/Banner';
import Chapters, { Chapter } from '@/components/Chapters';
import ImageGallery from '@/components/ImageGallery';
import { Reveal } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { siteStats } from '@/lib/siteStats';

const chapters: Chapter[] = [
  {
    title: 'A Modest Beginning',
    tagline: 'Zero Investment, Maximum Ambition',
    description: `${siteStats.years} years ago, Rtn Thamizhachi Priya Govindaraj embarked on her entrepreneurial odyssey with a modest yet profound vision - to offer quality education and empowerment. What sets her story apart is that she initiated this transformative journey right from the comfort of her own home, as a newlywed with two little daughters. But this didn’t deter her. What's truly remarkable is that she began with no initial investments and a heart full of ambition.`,
  },
  {
    title: 'A Conglomerate Under One Roof',
    tagline: 'Rtn Thamizhachi Priya Govindaraj’s Vision',
    description: `Rtn Thamizhachi Priya Govindaraj's influence extends far and wide across the state of Tamil Nadu. She is the visionary behind an empire of successful businesses, all thoughtfully united under one roof. These enterprises collectively offer an impressive repertoire of more than ${siteStats.courses} courses, imparting vital knowledge and skills to eager learners.`,
  },
  {
    title: 'Expanding Horizons',
    tagline: 'Four Branches and Four Franchises',
    description:
      "Her entrepreneurial prowess doesn't stop at teaching and mentoring. With four thriving branches already established and four franchises successfully granted, her reach continues to grow. Rtn Thamizhachi Priya Govindaraj's influence now touches the lives of more individuals and communities than ever before.",
  },
  {
    title: 'A Multitalented Force',
    tagline: 'Beyond Entrepreneurship',
    description:
      "Her remarkable skills extend far beyond entrepreneurship. She's a professional in Bharathanatyam, Western Dance, and holds licenses for Zumba, Keyboard, Guitar, Drums, Carnatic Vocal, Tailoring, Aariwork, and more. Her academic achievements include a B.Com. from Holy Cross College Trichy, an M.Com. from Bharathidasan University, and (C.A.)Inter Graduate status. Her interviews in leading newspapers and magazines reflect her exceptional talents. She has also won “Mrs. Congeniality Tamilnadu 2021 Title Winner”. Her prowess extends to the medical transcription field.",
  },
  {
    title: 'Empowering Women',
    tagline: 'FETTACK: Protection Against Female Attacks',
    description:
      'In addition to her professional success, Priya\'s commitment to societal well-being shines through her training of over 9,500 girls and women through the "FETTACK- Protection Against Female Attacks" program, offering self-defense skills against harassment and theft.',
  },
  {
    title: 'Rotary and Community Leader',
    tagline: 'Exemplifying Dedication and Service',
    description:
      "Rtn Thamizhachi Priya Govindaraj exemplifies the true spirit of dedication and service. Throughout her Rotary journey, she has donned various hats, serving as a Charter President twice introducing more than 65 women into Rotary and District Chairman for numerous initiatives and now Assistant Governor for the year 2024-25. But what truly sets her apart is her unwavering commitment to making a positive impact. Thamizhachi Priya's clubs have achieved remarkable feats, including a coveted mention in the Guinness Book of Records. Her relentless pursuit of excellence and her passion for Rotary have not only set her apart but have also inspired many to follow in her footsteps.",
  },
  {
    title: 'Family of Achievers',
    tagline: 'Behind Every Great Leader',
    description:
      "Behind every great leader, there's often an unsung hero, and in this case, it's her husband, Rotarian Govindaraj. An Auditor by profession, he has been the rock-solid support behind Thamizhachi Priya's journey. His quiet strength and dedication have undoubtedly played a significant role in her success. But the legacy of excellence doesn't end with them. Their daughters, Haritha and Aakshitha, have inherited their parents' incredible talents and a drive to make a difference. They've excelled in both academics and the arts, showcasing a versatility that is truly commendable. Haritha's achievements, from being a ‘School Topper with multiple centums’ to earning the title of ‘Miss Talented Tamil Nadu in 2021’, are a testament to her outstanding abilities.",
  },
  {
    title: 'Discover the Lessons',
    tagline: 'Unraveling the Secrets to Success',
    description:
      "As we delve deeper into Rtn Thamizhachi Priya Govindaraj's remarkable journey, we unravel the secrets to her extraordinary success. Join us in celebrating this outstanding entrepreneur who is changing lives and shaping the future of Tamil Nadu, one course at a time. Her story is an inspiration to all who Dare to Dream, Dare to Start, and Dare to Succeed.",
  },
];

const Founder = ({ images }: { images: string[] }) => {
  const urls = images.map((image) => `/storage/${image}`);

  const chapterImages = urls.slice(1, chapters.length + 1);
  const galleryImages = urls.slice(chapters.length + 1);

  return (
    <>
      <Banner
        image={urls[0]}
        alt="Rtn Thamizhachi Priya Govindaraj, founder of Samuthrika Academy"
        tagline="Empowering dreams"
        title="About CEO & Founder"
        description="The journey of Rtn Thamizhachi Priya Govindaraj, from a zero-investment beginning to a trailblazing entrepreneur with over 50 awards."
      >
        <ShareDialog
          url={window.location.href}
          title="About Rtn Thamizhachi Priya Govindaraj"
          content="The Journey of Rtn Thamizhachi Priya Govindaraj, From a Zero-Investment Beginning to a Trailblazing Entrepreneur with Over 50 Awards."
        />
      </Banner>

      {/*
        The introduction and the film were previously passed into Banner as
        children, which put a 16:9 video inside a header panel sized for a
        paragraph. They get their own band instead.
      */}
      <Band tone="indigo" pattern="stitch" patternOpacity="medium">
        <SectionLayout width="prose" space="lg">
          <Reveal className="flex flex-col gap-6">
            <Typography.Body className="text-indigo-foreground/85">
              In the annals of entrepreneurship, there emerges a beacon of
              inspiration and innovation, Rtn Thamizhachi Priya Govindaraj. Her
              journey, spanning the last {siteStats.years} years, is a testament
              to the extraordinary possibilities that passion, determination,
              and an indomitable spirit can unlock.
            </Typography.Body>

            <div className="aspect-video w-full overflow-hidden rounded-xl shadow-pop">
              <iframe
                className="size-full"
                src="https://www.youtube.com/embed/H2iIIsXqED0?si=tR6jpVtgM8-Y-DTv"
                title="Rtn Thamizhachi Priya Govindaraj — her story"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </Reveal>
        </SectionLayout>
      </Band>

      <Band tone="raised" pattern="lattice">
        <SectionLayout space="lg">
          <Chapters chapters={chapters} images={chapterImages} />
        </SectionLayout>
      </Band>

      {galleryImages.length > 0 && (
        <Band tone="raised" seam>
          <SectionLayout space="lg">
            <Reveal className="flex flex-col gap-3">
              <Typography.Eyebrow>Along the way</Typography.Eyebrow>
              <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
                Moments from the journey
              </Typography.Display2>
            </Reveal>

            <ImageGallery
              images={galleryImages}
              alt="Photographs from the founder's journey"
            />
          </SectionLayout>
        </Band>
      )}
    </>
  );
};

export default Founder;
